import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import SupplierFormDialog from "@/components/SupplierFormDialog";
import SupplierSpendDetailDialog from "@/components/SupplierSpendDetailDialog";
import SupplierDetailDialog from "@/components/SupplierDetailDialog";
import TechnicianFormDialog from "@/components/TechnicianFormDialog";
import ExportButtons from "@/components/ExportButtons";
import { useNavigate } from "react-router-dom";
import { Plus, Edit3, Search, Mail, Phone, Eye, ExternalLink, Printer, X, CheckSquare, Square } from "lucide-react";

const Suppliers = () => {
  const { isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reports, setReports] = useState({});
  const [reliability, setReliability] = useState({});
  const [spendDetail, setSpendDetail] = useState(null); // {id, name}
  const [detailFor, setDetailFor] = useState(null); // supplier_id
  const [techFormOpenFor, setTechFormOpenFor] = useState(null);
  // v200 — Bulk-assign Primary Owner. `selected` holds the tick-boxed
  // supplier ids; when non-empty the floating action bar appears.
  const [selected, setSelected] = useState(new Set());
  const [departments, setDepartments] = useState([]);
  const [bulkDept, setBulkDept] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const nav = useNavigate();

  const toggleSelected = (sid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());
  const selectAllVisible = () => {
    setSelected(new Set(filtered.map((s) => s.id)));
  };
  const runBulkAssign = async () => {
    if (!selected.size) return;
    if (bulkDept === "__unset__" ? false : !bulkDept) {
      return toast.error("Pick a department (or 'Clear owner')");
    }
    setBulkBusy(true);
    try {
      const dept = bulkDept === "__unset__" ? null : bulkDept;
      const { data } = await api.post("/suppliers/bulk-primary-department", {
        ids: [...selected],
        primary_department: dept,
      });
      toast.success(
        `${data.modified} supplier${data.modified === 1 ? "" : "s"} → ${dept || "no owner"}`,
      );
      clearSelected();
      setBulkDept("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/suppliers", { params: { include_inactive: true } });
      setItems(data);
    } catch {
      toast.error("Could not load suppliers");
    }
  };
  useEffect(() => {
    load();
    api.get("/suppliers/reliability")
      .then(({ data }) => {
        const map = {};
        (data.items || []).forEach((r) => {
          map[r.supplier_id] = r;
        });
        setReliability(map);
      })
      .catch(() => {});
    api.get("/departments")
      .then(({ data }) => setDepartments(data || []))
      .catch(() => setDepartments([]));
  }, []);

  const loadReport = async (sid) => {
    if (reports[sid]) return;
    try {
      const { data } = await api.get(`/reports/supplier/${sid}`, { params: { days: 90 } });
      setReports((r) => ({ ...r, [sid]: data }));
    } catch (e) {
      console.error("Failed to load supplier report:", e);
    }
  };

  useEffect(() => {
    items.forEach((s) => s.is_active && loadReport(s.id));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.contact_name || "").toLowerCase().includes(q) ||
        (s.email || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="suppliers-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Vendors & PO destinations</div>
          <h1 className="font-display text-5xl leading-none mt-2">Suppliers</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href="/supplier-add-print"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="supplier-print-add-btn"
            className="nb-btn bg-white px-3 py-4 flex items-center gap-2 no-underline text-sm font-bold"
            title="Print blank forms to fill in when meeting a new supplier — transcribe to the app afterwards"
          >
            <Printer size={14} strokeWidth={2.5} /> Print add-supplier form
          </a>
          <ExportButtons
            entity="suppliers"
            rows={items}
            testidPrefix="supplier-export"
          />
          <button
            data-testid="supplier-add-btn"
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
            className="nb-btn nb-btn-primary px-5 py-4 flex items-center gap-2"
          >
            <Plus size={20} strokeWidth={2.5} /> Add supplier
          </button>
        </div>
      </div>

      <div className="relative mb-6">
        <Search
          size={20}
          strokeWidth={2.5}
          className="absolute left-3.5 top-1/2 -translate-y-1/2"
        />
        <input
          data-testid="supplier-search-input"
          className="nb-input pl-12 h-14 text-lg"
          placeholder="Search suppliers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="suppliers-grid"
      >
        {isManager && filtered.length > 0 && (
          <div className="col-span-full flex items-center gap-2 flex-wrap text-xs">
            <button
              data-testid="supplier-select-all"
              onClick={selected.size === filtered.length ? clearSelected : selectAllVisible}
              className="nb-btn bg-white px-2.5 py-1 font-bold flex items-center gap-1.5"
            >
              {selected.size === filtered.length ? (
                <CheckSquare size={12} strokeWidth={2.5} />
              ) : (
                <Square size={12} strokeWidth={2.5} />
              )}
              {selected.size === filtered.length ? "Deselect all" : "Select all"}
            </button>
            <span className="text-[#607087]">
              {selected.size > 0
                ? `${selected.size} selected`
                : "Tick suppliers to bulk-assign a Primary Owner"}
            </span>
          </div>
        )}
        {filtered.map((s) => {
          const r = reports[s.id];
          return (
            <div
              key={s.id}
              className="nb-card p-5 flex flex-col gap-2"
              data-testid={`supplier-card-${s.id}`}
              style={{ opacity: s.is_active ? 1 : 0.5 }}
            >
              <div className="flex items-start justify-between">
                {isManager && (
                  <button
                    data-testid={`supplier-select-${s.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelected(s.id);
                    }}
                    className="p-1 -ml-1 mr-1 mt-0.5 shrink-0"
                    aria-label={
                      selected.has(s.id) ? "Deselect supplier" : "Select supplier"
                    }
                    title="Select for bulk actions"
                  >
                    {selected.has(s.id) ? (
                      <CheckSquare
                        size={20}
                        strokeWidth={2.5}
                        className="text-[#0A3A7A]"
                      />
                    ) : (
                      <Square size={20} strokeWidth={2.25} className="text-[#607087]" />
                    )}
                  </button>
                )}
                <button
                  data-testid={`supplier-view-${s.id}`}
                  onClick={() => setDetailFor(s.id)}
                  className="flex-1 min-w-0 text-left hover:opacity-80"
                  title="Open 360° view"
                >
                  <div className="font-display text-xl truncate flex items-center gap-1.5">
                    {s.name}
                    <Eye
                      size={13}
                      strokeWidth={2.5}
                      className="text-[#228EBF] flex-shrink-0"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                    {s.contact_name && (
                      <span className="text-sm text-[#607087]">{s.contact_name}</span>
                    )}
                    {s.primary_department && (
                      <span
                        data-testid={`supplier-primary-dept-${s.id}`}
                        className="text-[10px] font-bold uppercase tracking-wider bg-[#DFEEF7] border-2 border-[#0A3A7A] text-[#0A3A7A] rounded px-1.5 py-0.5"
                        title="Primary owner — used by the Orders / PO department filter"
                      >
                        {s.primary_department}
                      </span>
                    )}
                  </div>
                </button>
                <button
                  data-testid={`supplier-edit-${s.id}`}
                  onClick={() => {
                    setEditing(s);
                    setOpen(true);
                  }}
                  className="nb-btn bg-white p-2"
                  aria-label="edit"
                >
                  <Edit3 size={14} strokeWidth={2.5} />
                </button>
              </div>
              {s.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail size={14} strokeWidth={2.5} /> {s.email}
                </div>
              )}
              {s.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone size={14} strokeWidth={2.5} /> {s.phone}
                </div>
              )}
              {s.website && (
                <a
                  data-testid={`supplier-website-${s.id}`}
                  href={
                    /^https?:\/\//i.test(s.website)
                      ? s.website
                      : `https://${s.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#228EBF] hover:underline break-all"
                  onClick={(e) => e.stopPropagation()}
                  title="Opens supplier website in a new tab — order stock online"
                >
                  <ExternalLink size={14} strokeWidth={2.5} /> {s.website}
                </a>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="border-2 border-black rounded-lg p-2">
                  <div className="label-eyebrow">Items</div>
                  <div className="font-mono-num font-bold text-xl">
                    {r?.items?.length ?? "—"}
                  </div>
                </div>
                <div
                  className="border-2 border-black rounded-lg p-2"
                  style={{ background: "#DFEEF7" }}
                >
                  <div className="flex items-center justify-between">
                    <div className="label-eyebrow">90d Spend</div>
                    <button
                      data-testid={`supplier-spend-detail-${s.id}`}
                      onClick={() => setSpendDetail({ id: s.id, name: s.name })}
                      className="nb-btn bg-white p-1 -mr-1"
                      aria-label="view spend detail"
                      title="Show every movement contributing to this total"
                    >
                      <Eye size={11} strokeWidth={2.5} />
                    </button>
                  </div>
                  <div className="font-mono-num font-bold text-xl">
                    ${(r?.total_spend ?? 0).toFixed(0)}
                  </div>
                </div>
              </div>
              {reliability[s.id] && (
                <div
                  data-testid={`supplier-reliability-${s.id}`}
                  className={`mt-1 border-2 rounded-lg p-2 flex items-baseline justify-between ${
                    reliability[s.id].on_time_rate >= 90
                      ? "border-[#1F6F3E] bg-[#E6F4EA]"
                      : reliability[s.id].on_time_rate >= 60
                      ? "border-[#7A4A00] bg-[#FFF7DB]"
                      : "border-[#8A1F1F] bg-[#FCE4E4]"
                  }`}
                >
                  <div>
                    <div className="label-eyebrow">On-time deliveries</div>
                    <div className="text-xs text-[#607087]">
                      {reliability[s.id].on_time}/{reliability[s.id].total} received on/before ETA
                    </div>
                  </div>
                  <div className="font-mono-num font-bold text-xl">
                    {reliability[s.id].on_time_rate}%
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full nb-card p-10 text-center">
            <div className="font-display text-2xl">No suppliers yet</div>
            <div className="text-sm text-[#607087] mt-1">
              Add a supplier so you can email POs and track per-supplier spend.
            </div>
          </div>
        )}
      </div>

      {isManager && selected.size > 0 && (
        <div
          data-testid="supplier-bulk-bar"
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 nb-card p-3 flex items-center gap-2 shadow-xl bg-[#111] text-white border-white flex-wrap max-w-[95vw]"
        >
          <span className="font-bold text-sm px-1.5">
            {selected.size} selected
          </span>
          <span className="text-[11px] text-[#B8C3D0] px-1">Set primary owner →</span>
          <select
            data-testid="supplier-bulk-dept"
            className="nb-input py-1.5 px-2 text-sm text-black min-w-[160px]"
            value={bulkDept}
            onChange={(e) => setBulkDept(e.target.value)}
          >
            <option value="">— pick a dept —</option>
            {departments.map((d) => (
              <option key={d.id} value={d.name}>{d.name}</option>
            ))}
            <option value="__unset__">Clear owner (visible only under "All depts")</option>
          </select>
          <button
            data-testid="supplier-bulk-apply"
            onClick={runBulkAssign}
            disabled={bulkBusy || !bulkDept}
            className="nb-btn nb-btn-primary px-3 py-1.5 text-sm font-bold"
          >
            Apply
          </button>
          <button
            data-testid="supplier-bulk-clear"
            onClick={clearSelected}
            className="nb-btn bg-white px-2 py-1.5 text-sm text-black"
            title="Clear selection"
          >
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}

      <SupplierFormDialog
        open={open}
        initial={editing}
        onClose={() => setOpen(false)}
        onSaved={load}
        onDelete={load}
      />

      <SupplierSpendDetailDialog
        open={!!spendDetail}
        supplierId={spendDetail?.id}
        supplierName={spendDetail?.name}
        onClose={() => setSpendDetail(null)}
        onFixed={() => {
          // Refresh the supplier card summary so its 90d Spend number
          // reflects the just-corrected snapshot immediately.
          setReports((prev) => {
            const next = { ...prev };
            delete next[spendDetail.id];
            return next;
          });
          if (spendDetail?.id) {
            api
              .get(`/reports/supplier/${spendDetail.id}`, { params: { days: 90 } })
              .then(({ data }) =>
                setReports((r) => ({ ...r, [spendDetail.id]: data })),
              )
              .catch(() => {});
          }
        }}
      />

      <SupplierDetailDialog
        open={!!detailFor}
        supplierId={detailFor}
        onClose={() => setDetailFor(null)}
        onEdit={(s) => {
          setDetailFor(null);
          setEditing(s);
          setOpen(true);
        }}
        onAddTechnician={(s) => {
          setTechFormOpenFor(s);
        }}
        onLinkMachine={() => {
          // Sends the manager to Technicians where they can pick a tech and
          // hit the Machinery multi-select. Detail dialog stays open so they
          // can jump back.
          nav("/technicians");
        }}
      />

      {/* Prefilled technician form launched from the 360° dialog. */}
      <TechnicianFormDialog
        open={!!techFormOpenFor}
        initial={
          techFormOpenFor
            ? {
                supplier_id: techFormOpenFor.id,
                supplier_name: techFormOpenFor.name,
              }
            : null
        }
        organisations={
          techFormOpenFor ? [{ id: techFormOpenFor.id, name: techFormOpenFor.name }] : []
        }
        onClose={() => setTechFormOpenFor(null)}
        onSaved={() => {
          // Refresh the 360° view by nudging the state (re-triggers the
          // detail fetch inside SupplierDetailDialog via its supplierId dep).
          const sid = techFormOpenFor?.id;
          setTechFormOpenFor(null);
          if (sid) {
            setDetailFor(null);
            setTimeout(() => setDetailFor(sid), 100);
          }
        }}
      />
    </div>
  );
};

export default Suppliers;
